import React from 'react'
import { AlertMessage, Button, Dialog, Typography, icons, theme } from 'cdh-data-portal-theme'
import type { DataGovernor } from '../../../../api/client'
import type { useStyles } from '../DataGovernorsPage.style'
import { businessObjectFriendlyName, hubFriendlyName, localText as sharedText } from '../DataGovernorsPage.static'
import { useRemoveGovernorDialog } from './RemoveGovernorDialog.logic'
import type { RemoveGovernorOutcome } from './RemoveGovernorDialog.logic'
import { localText, removeGovernorConfirmationText } from './RemoveGovernorDialog.static'

export interface RemoveGovernorDialogProps {
  readonly classes: ReturnType<typeof useStyles>['classes']
  readonly governor: DataGovernor | null
  readonly environment: string
  readonly hubFriendlyNames: Readonly<Record<string, string>>
  readonly onClose: () => void
  readonly onGovernorRemoved: (outcome: RemoveGovernorOutcome) => void
}

export const RemoveGovernorDialog: React.FC<RemoveGovernorDialogProps> = ({
  classes,
  governor,
  environment,
  hubFriendlyNames,
  onClose,
  onGovernorRemoved,
}) => {
  const { dialogError, isMutationInProgress, closeDialog, submit } = useRemoveGovernorDialog({
    governor,
    environment,
    onClose,
    onGovernorRemoved,
  })

  return (
    <Dialog
      actions={
        <div className={classes.dialogActions}>
          <Button
            isDisabled={isMutationInProgress}
            onClick={closeDialog}
            title={sharedText.cancelButton}
            variant="text"
          />
          <Button
            icon={icons.trash}
            isLoading={isMutationInProgress}
            onClick={() => void submit()}
            title={localText.removeGovernorButton}
            variant="alert"
          />
        </div>
      }
      isOpen={governor !== null}
      minWidth={0}
      onClose={closeDialog}
      title={localText.removeGovernorDialogTitle}
      width={620}
    >
      <div className={classes.dialogFields}>
        {governor && (
          <Typography variant="body" color={theme.textColors.primary}>
            {removeGovernorConfirmationText(
              governor.email,
              businessObjectFriendlyName(governor.business_object),
              hubFriendlyName(governor.hub, hubFriendlyNames),
            )}
          </Typography>
        )}
        <AlertMessage
          className={classes.dialogWarning}
          message={localText.removalWarningMessage}
          title={localText.confirmRemovalTitle}
          variant="warning"
        />
        {dialogError && (
          <AlertMessage message={dialogError} title={localText.unableToRemoveGovernorTitle} variant="error" />
        )}
      </div>
    </Dialog>
  )
}
